import React, { useEffect, useRef, useState } from "react";
import { Loader } from "google-maps";
import { CircularProgress } from "@material-ui/core";
import { config } from "../core";

// Log-scaled heat colours for county fill intensity.
const HEAT_COLORS = [
  { threshold: 0.0, color: "#fef9c3" },
  { threshold: 0.2, color: "#fde047" },
  { threshold: 0.4, color: "#fbbf24" },
  { threshold: 0.55, color: "#f97316" },
  { threshold: 0.7, color: "#ef4444" },
  { threshold: 0.85, color: "#b91c1c" },
  { threshold: 0.97, color: "#7f1d1d" },
];

function heatColor(t) {
  let color = HEAT_COLORS[0].color;
  for (let i = 1; i < HEAT_COLORS.length; i++) {
    if (t >= HEAT_COLORS[i].threshold) {
      color = HEAT_COLORS[i].color;
    } else {
      break;
    }
  }
  return color;
}

function logRatio(count, max) {
  if (count <= 0 || max <= 1) {return 0;}
  return Math.log(count) / Math.log(max);
}

const countyKey = (state, county) => `${state}|${county}`.toLowerCase().replace(/[^a-z0-9]/g, "");

// CountyChoroplethMap renders a Google Map with the given counties shaded by the
// selected metric. `counties` is the AreaUtilisationDetail.counties array; `metric`
// is "complete" | "outstanding" | "incoming" | "scheduled".
const CountyChoroplethMap = ({ counties, metric }) => {
  const mapDivRef = useRef(null);
  const [google, setGoogle] = useState(null);
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dataLayerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const lastFitRef = useRef("");

  // Build a county key -> stats lookup once per counties change.
  const stats = React.useMemo(() => {
    const m = new Map();
    counties.forEach((c) => m.set(countyKey(c.state, c.county), c));
    return m;
  }, [counties]);

  const maxValue = React.useMemo(() => {
    let max = 0;
    counties.forEach((c) => { if ((c[metric] || 0) > max) {max = c[metric] || 0;} });
    return max;
  }, [counties, metric]);

  // Init the map once.
  useEffect(() => {
    let cancelled = false;
    const loader = new Loader(config.mapsKeyOpenMap);
    loader.load().then((g) => {
      if (cancelled) {return;}
      setGoogle(g);
      const m = new g.maps.Map(mapDivRef.current, {
        center: { lat: 30, lng: -85 },
        zoom: 5,
        mapTypeControl: false,
        streetViewControl: false,
        scaleControl: true,
      });
      setMap(m);
      infoWindowRef.current = new g.maps.InfoWindow();
    }).catch(() => {
      if (!cancelled) {setError("Failed to load map."); setLoading(false);}
    });
    return () => { cancelled = true; };
  }, []);

  // Render / re-style the county layer whenever data or metric changes.
  useEffect(() => {
    if (!map || !google || counties.length === 0) {return;}
    setLoading(true);
    setError(null);

    let cancelled = false;
    fetch(`${config.staticPath}/geo/counties.geojson`)
      .then((res) => res.json())
      .then((geoJson) => {
        if (cancelled) {return;}

        if (dataLayerRef.current) {
          dataLayerRef.current.setMap(null);
          dataLayerRef.current = null;
        }

        const dataLayer = new google.maps.Data();
        const bounds = new google.maps.LatLngBounds();

        geoJson.features.forEach((feature) => {
          const p = feature.properties;
          const key = countyKey(p.state, p.NAME);
          const c = stats.get(key);
          if (!c) {return;} // only counties in this work area

          const value = c[metric] || 0;
          const ratio = logRatio(value, maxValue);
          const clone = JSON.parse(JSON.stringify(feature));
          const added = dataLayer.addGeoJson(clone);
          added.forEach((f) => {
            f.setProperty("scheduled", c.scheduled || 0);
            f.setProperty("complete", c.complete || 0);
            f.setProperty("outstanding", c.outstanding || 0);
            f.setProperty("incoming", c.incoming || 0);
            f.setProperty("ratio", ratio);
            f.setProperty("value", value);
            f.setProperty("countyName", p.NAME);
            f.setProperty("state", p.state);
            f.getGeometry().forEachLatLng((ll) => bounds.extend(ll));
          });
        });

        dataLayer.setStyle((f) => {
          const value = f.getProperty("value") || 0;
          const ratio = f.getProperty("ratio") || 0;
          return {
            fillColor: value > 0 ? heatColor(ratio) : "#e5e7eb",
            fillOpacity: value > 0 ? 0.7 : 0.25,
            strokeColor: "#374151",
            strokeWeight: 1,
          };
        });

        dataLayer.addListener("click", (event) => {
          const f = event.feature;
          const name = f.getProperty("countyName") || "";
          const state = f.getProperty("state") || "";
          const complete = f.getProperty("complete") || 0;
          const outstanding = f.getProperty("outstanding") || 0;
          const incoming = f.getProperty("incoming") || 0;
          infoWindowRef.current.setContent(
            `<div style="min-width:160px">` +
              `<div style="font-weight:700;font-size:14px">${name}, ${state}</div>` +
              `<div style="margin-top:4px;font-size:12px;color:#374151">` +
                (metric === "scheduled" ? `Policies booked: <b>${f.getProperty("scheduled") || 0}</b>` :
                  `Complete: <b>${complete}</b><br>Outstanding: <b>${outstanding}</b><br>Incoming: <b>${incoming}</b>`) +
              `</div>` +
            `</div>`
          );
          infoWindowRef.current.setPosition(event.latLng);
          infoWindowRef.current.open(map);
        });

        dataLayer.setMap(map);
        dataLayerRef.current = dataLayer;

        if (!bounds.isEmpty()) {
          const sw = bounds.getSouthWest().toUrlValue(6);
          const ne = bounds.getNorthEast().toUrlValue(6);
          const sig = `${sw}|${ne}`;
          if (sig !== lastFitRef.current) {
            map.fitBounds(bounds, 12);
            lastFitRef.current = sig;
          }
        }
        setLoading(false);
      })
      .catch(() => { if (!cancelled) {setError("Failed to load county boundaries."); setLoading(false);} });

    return () => {
      cancelled = true;
      if (dataLayerRef.current) {
        dataLayerRef.current.setMap(null);
        dataLayerRef.current = null;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, [map, google, counties, stats, metric, maxValue]);

  return (
    <div className="tw-relative" style={{ height: 360 }}>
      <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
      {loading && 
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white tw-bg-opacity-60">
          <CircularProgress />
        </div>
      }
      {error && 
        <div className="tw-absolute tw-top-2 tw-left-2 tw-text-red-600 tw-text-sm tw-bg-white tw-px-2 tw-py-1 tw-rounded">
          {error}
        </div>
      }
    </div>
  );
};

export default CountyChoroplethMap;